import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight, PieChart, Coins, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FractionalPortfolio() {
  const { user, loading, authHeaders } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/fractional/my-shares`, {
      headers: authHeaders(), withCredentials: true,
    }).then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line
  }, [user]);

  if (loading) return <div className="px-6 md:px-24 py-32 text-center text-[#A3A3A3]">Verifying ledger…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!data) return <div className="px-6 md:px-24 py-32 text-center"><Loader2 size={20} className="animate-spin text-[#D4AF37]" /></div>;

  const { summary, holdings, dividends } = data;

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <PieChart size={14} strokeWidth={1.5} /> Sovereign Portfolio
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Your slice of<br />
            <span className="font-display italic text-[#D4AF37]">the corridor.</span>
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 border-t border-b border-[#D4AF37]/15 py-8">
        <Stat label="Total Invested" value={fmt$(summary.total_invested_usd)} accent />
        <Stat label="Total Shares" value={summary.total_shares} />
        <Stat label="Dividends Received" value={fmt$(summary.total_dividends_received_usd)} />
        <Stat label="Active Offerings" value={summary.active_offerings} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Holdings</div>
          {holdings.length === 0 ? (
            <div className="sov-border bg-[#0A0A0A] p-12 text-center">
              <Coins size={24} className="text-[#D4AF37] mx-auto mb-4" strokeWidth={1} />
              <p className="text-sm text-[#A3A3A3] font-light mb-6">No sovereign shares yet.</p>
              <Link to="/fractional" className="btn-sovereign"><span>Browse Offerings</span> <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {holdings.map((h) => (
                <div key={h.id} className="sov-border bg-[#0A0A0A] p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-light tracking-wide mb-1">{h.property_title}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                        {h.shares} shares @ {fmt$(h.share_price_usd)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-2xl text-[#D4AF37]">{fmt$(h.cost_usd)}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-1">
                        {new Date(h.purchased_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Dividend Ledger</div>
          {dividends.length === 0 ? (
            <div className="sov-border bg-[#0A0A0A] p-8 text-center">
              <p className="text-sm text-[#A3A3A3] font-light">
                Distributions will appear here at the end of each fiscal period.
              </p>
            </div>
          ) : (
            <div className="sov-border bg-[#0A0A0A] p-6 space-y-3">
              {dividends.map((d) => (
                <div key={d.id} className="flex items-center justify-between border-b border-[#D4AF37]/15 pb-3 last:border-b-0">
                  <div>
                    <div className="text-sm font-light">{d.property_title}</div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-1">{d.period_label}</div>
                  </div>
                  <div className="font-display text-lg text-[#D4AF37]">+{fmt$(d.amount_usd)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{label}</div>
    <div className={`metric-number text-3xl md:text-4xl ${accent ? 'text-[#D4AF37]' : ''}`}>{value}</div>
  </div>
);
